import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

export const usePostVote = (campaignId) => {
    const [message, setMessage] = useState('');
    const [isSubmitVisible, setIsSubmitVisible] = useState(true);
    const [currentVote, setCurrentVote] = useState(null);
    const [isLoadingVote, setIsLoadingVote] = useState(false);
    const { getAccessTokenSilently, user } = useAuth0();
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        let isMounted = true;

        const loadCurrentVote = async () => {
            if (!campaignId) return;
            setIsLoadingVote(true);
            try {
                const token = await getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
                });
                const response = await fetch(`${apiUrl}/api/votes/campaign/${campaignId}/mine`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error(`Unable to load your vote (${response.status})`);
                const responseText = await response.text();
                const vote = responseText ? JSON.parse(responseText) : null;
                if (isMounted) setCurrentVote(vote || null);
            } catch (error) {
                console.error('Error loading vote:', error);
                if (isMounted) setMessage('Your saved vote could not be loaded. Please refresh and try again.');
            } finally {
                if (isMounted) setIsLoadingVote(false);
            }
        };

        loadCurrentVote();
        return () => { isMounted = false; };
    }, [apiUrl, campaignId, getAccessTokenSilently]);

    const postVote = async (campaignId, approvalStatus, voteReason, createdOn) => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            const vote1 = approvalStatus === 'Approve';
            
            const response = await fetch(`${apiUrl}/api/votes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    campaignId: campaignId,
                    userId: user.sub, // Use the user's ID from Auth0
                    vote1: vote1,
                    voteReason: voteReason,
                    createdOn: createdOn,
                }),
            });

            if (response.ok) {
                const savedVote = await response.json();
                setCurrentVote(savedVote);
                setIsSubmitVisible(true);
                setMessage(currentVote ? 'Vote successfully updated' : 'Vote successfully submitted');
                return savedVote;
            } else {
                console.error('Error submitting vote:', response);
                const problem = await response.json().catch(() => null);
                setMessage(problem?.detail || 'Error submitting vote');
                return null;
            }
        } catch (error) {
            console.error('Error:', error);
            setMessage('Unexpected error occurred');
        }
    };

    return { message, isSubmitVisible, currentVote, isLoadingVote, postVote, setIsSubmitVisible, setMessage };
};
